import { useState } from "react";
import { useParams, Link } from "wouter";
import { useQuery } from "@tanstack/react-query";
import { Receipt, AlertCircle, ArrowLeft, Download, Eye, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { formatXof, formatDateTime, statusLabel } from "@/lib/format";
import { getPaiementByToken, getRecuPdfUrl, ApiError } from "@/lib/api";
import { getPaiementPublicQueryKey } from "@/lib/backoffice-hooks";
import { useCurrentUser } from "@/lib/auth";
import ClaimAccountCard from "@/components/paiement/ClaimAccountCard";
import RecuPreviewDialog from "@/components/RecuPreviewDialog";

const TERMINAL_STATUSES = new Set(["SUCCESS", "FAILED", "CANCELLED"]);

export default function PaiementDetail() {
  const { paiementId } = useParams();
  const { isAuthenticated } = useCurrentUser();
  // `paiementId` in the URL is actually the guest token (returned by the
  // initiate endpoint). For authenticated flows we'd take a different code
  // path — out of scope for S3.
  const token =
    paiementId ??
    (typeof sessionStorage !== "undefined"
      ? sessionStorage.getItem(`catho.guestToken.${paiementId}`)
      : null) ??
    "";

  const { data: paiement, isLoading, isError } = useQuery({
    // Reuse the shared key so a successful claim invalidates this query.
    queryKey: getPaiementPublicQueryKey(token),
    enabled: !!token,
    queryFn: () => getPaiementByToken(token),
    // Un 404 « token inconnu » = lien invalide/expiré → état terminal : ne pas
    // retenter (un 429/5xx transitoire reste retenté).
    retry: (count, err) =>
      !(err instanceof ApiError && err.status === 404) && count < 2,
    // Ne JAMAIS poller un lookup en échec : sinon la page martèle l'endpoint
    // toutes les 2 s (404 en boucle, console inondée). On ne poursuit le polling
    // que tant que le paiement existe et n'est pas dans un statut terminal.
    refetchInterval: (q) => {
      if (q.state.status === "error") return false;
      const s = (q.state.data as { statut?: string } | undefined)?.statut;
      return s && TERMINAL_STATUSES.has(s) ? false : 2000;
    },
    // Suivi de paiement : le polling 2 s suffit ; pas de refetch au focus (qui
    // relancerait un 404 sur un lien invalide à chaque retour sur l'onglet).
    refetchOnWindowFocus: false,
  });

  if (isLoading) {
    return <div className="p-12 text-center text-navy/50">Chargement…</div>;
  }
  if (isError || !paiement) {
    return (
      <div className="p-12 text-center text-red-500">
        Paiement introuvable.
        <p className="mt-2 text-sm text-navy/60">
          Vérifiez le lien reçu par email ou SMS.
        </p>
      </div>
    );
  }

  const status = statusLabel(paiement.statut);

  return (
    <div className="max-w-2xl mx-auto px-6 py-12">
      <Link
        href={isAuthenticated ? "/dashboard" : "/"}
        className="inline-flex items-center gap-2 text-navy/60 hover:text-navy font-bold text-sm mb-6 transition-colors"
      >
        <ArrowLeft size={16} />{" "}
        {isAuthenticated ? "Retour au tableau de bord" : "Retour à l'accueil"}
      </Link>

      <div className="bg-white rounded-3xl p-8 border border-orange-50 shadow-sm relative overflow-hidden">
        <div
          className={`absolute top-0 right-0 w-32 h-32 rounded-bl-full -z-10 opacity-20 ${
            paiement.statut === "SUCCESS"
              ? "bg-sage"
              : paiement.statut === "FAILED"
                ? "bg-destructive"
                : "bg-gold"
          }`}
        />

        <div className="flex justify-between items-start mb-8">
          <div>
            <h1 className="text-[28px] leading-tight [font-family:'Cormorant_Garamond',serif] font-bold text-navy mb-1">
              Détail du paiement
            </h1>
            <p className="font-mono text-xs text-navy/50">
              {paiement.reference}
            </p>
          </div>
          <div
            className={`px-3 py-1.5 rounded-lg border font-bold text-sm ${status.badge}`}
          >
            {status.label}
          </div>
        </div>

        <div className="space-y-6">
          <div className="p-6 bg-cream rounded-2xl text-center">
            <p className="text-sm font-bold text-navy/60 uppercase tracking-wider mb-1">
              Montant
            </p>
            <p className="text-[44px] leading-none [font-family:'Cormorant_Garamond',serif] font-bold text-navy">
              {formatXof(paiement.montantXof)}
            </p>
            <p className="text-xs text-navy/50 mt-2 font-medium">
              Via {paiement.methode}
            </p>
          </div>

          <div className="grid sm:grid-cols-2 gap-4 text-sm">
            <div className="border border-orange-50 p-4 rounded-xl">
              <span className="text-navy/50 text-xs block mb-1">École</span>
              <strong className="text-navy">{paiement.ecole?.nom}</strong>
            </div>
            <div className="border border-orange-50 p-4 rounded-xl">
              <span className="text-navy/50 text-xs block mb-1">Motif</span>
              <strong className="text-navy">{paiement.motif?.libelle}</strong>
            </div>
            <div className="border border-orange-50 p-4 rounded-xl">
              <span className="text-navy/50 text-xs block mb-1">Élève</span>
              <strong className="text-navy">
                {[paiement.eleve?.nom, paiement.eleve?.prenoms]
                  .filter(Boolean)
                  .join(" ")}
              </strong>
              {paiement.eleve?.matricule && (
                <span className="block text-xs font-mono text-navy/40 mt-0.5">
                  {paiement.eleve.matricule}
                </span>
              )}
            </div>
            <div className="border border-orange-50 p-4 rounded-xl">
              <span className="text-navy/50 text-xs block mb-1">
                {paiement.paidAt ? "Payé le" : "Expire le"}
              </span>
              <strong className="text-navy">
                {formatDateTime(paiement.paidAt ?? paiement.expiresAt)}
              </strong>
            </div>
          </div>

          {paiement.statut === "SUCCESS" && (
            <DownloadRecuCard token={token} reference={paiement.reference} />
          )}

          {/* Mode dual : on ne propose la création de compte qu'aux invités
              (un utilisateur authentifié possède déjà un espace). */}
          {!isAuthenticated && paiement.canClaim && token && (
            <ClaimAccountCard
              token={token}
              payerEmail={paiement.payeur?.email}
              payerTelephone={paiement.payeur?.telephone}
            />
          )}

          {paiement.statut === "FAILED" && (
            <div className="mt-8 p-6 bg-destructive/5 border border-destructive/20 rounded-2xl flex flex-col items-center text-center">
              <AlertCircle className="text-destructive w-12 h-12 mb-3" />
              <h3 className="font-bold text-navy mb-4">Le paiement a échoué</h3>
              <Link href="/payer" className="w-full">
                <Button className="w-full bg-navy hover:bg-[#13204A] text-white font-semibold rounded-[14px]">
                  Réessayer
                </Button>
              </Link>
            </div>
          )}

          {!TERMINAL_STATUSES.has(paiement.statut) && (
            <div className="mt-4 p-4 bg-gold/5 border border-gold/20 rounded-2xl flex items-center gap-3 text-sm text-navy/80">
              <Loader2 className="animate-spin text-gold" size={20} />
              <span>
                Paiement en cours de traitement… cette page se met à jour
                automatiquement.
              </span>
            </div>
          )}
        </div>
      </div>

      {!isAuthenticated && !paiement.canClaim && (
        <div className="mt-8 p-4 rounded-2xl border border-dashed border-orange-100 text-xs text-navy/60 text-center">
          Votre reçu reste accessible à tout moment via le lien envoyé par email
          et SMS.
        </div>
      )}
    </div>
  );
}

function DownloadRecuCard({
  token,
  reference,
}: {
  token: string;
  reference: string;
}) {
  const [downloading, setDownloading] = useState(false);
  const [apercuOpen, setApercuOpen] = useState(false);

  async function handleDownload() {
    setDownloading(true);
    const url = getRecuPdfUrl(token);
    // Backend may return 425 Too Early while the PDF is being generated —
    // retry a few times with backoff before giving up.
    const maxAttempts = 6;
    let attempt = 0;
    let lastError: string | null = null;
    while (attempt < maxAttempts) {
      try {
        const res = await fetch(url);
        if (res.ok) {
          const blob = await res.blob();
          const objUrl = URL.createObjectURL(blob);
          const a = document.createElement("a");
          a.href = objUrl;
          a.download = `recu-${reference}.pdf`;
          document.body.appendChild(a);
          a.click();
          a.remove();
          URL.revokeObjectURL(objUrl);
          setDownloading(false);
          return;
        }
        if (res.status === 425 || res.status === 503) {
          attempt += 1;
          await new Promise((r) => setTimeout(r, 1500 * attempt));
          continue;
        }
        lastError = `HTTP ${res.status}`;
        break;
      } catch (err) {
        lastError = err instanceof Error ? err.message : "Erreur réseau";
        break;
      }
    }
    toast.error(
      lastError
        ? `Téléchargement impossible : ${lastError}`
        : "Le reçu n'est pas encore prêt. Réessayez dans quelques secondes.",
    );
    setDownloading(false);
  }

  return (
    <div className="mt-8 p-6 bg-sage/5 border border-sage/20 rounded-2xl flex flex-col items-center text-center">
      <Receipt className="text-sage w-12 h-12 mb-3" />
      <h3 className="font-bold text-navy mb-1">Reçu généré avec succès</h3>
      <p className="text-xs text-navy/60 font-mono mb-4">{reference}</p>
      <div className="flex flex-wrap items-center justify-center gap-2">
        <Button
          onClick={() => setApercuOpen(true)}
          variant="outline"
          className="border-sage/40 text-sage hover:bg-sage/10 font-bold"
          data-testid="button-apercu-recu"
        >
          <Eye size={16} className="mr-2" /> Aperçu
        </Button>
        <Button
          onClick={handleDownload}
          disabled={downloading}
          className="bg-sage hover:bg-[#8eb087] text-white font-bold"
        >
          {downloading ? (
            <>
              <Loader2 size={16} className="mr-2 animate-spin" /> Préparation…
            </>
          ) : (
            <>
              <Download size={16} className="mr-2" /> Télécharger le PDF
            </>
          )}
        </Button>
      </div>

      <RecuPreviewDialog
        open={apercuOpen}
        onOpenChange={setApercuOpen}
        source={{ kind: "token", token }}
        reference={reference}
      />
    </div>
  );
}
